import { Quote, Star, LinkedinIcon, Building2 } from 'lucide-react';

const testimonials = [
  {
    content: 'ZeeJay Labs didn\'t just build software—they transformed our entire business model. The $15M in new revenue we generated in year one paid for their services 10x over. Their team operates at a level I\'ve rarely seen in 20 years of tech leadership.',
    author: 'Sarah Chen',
    role: 'Chief Technology Officer',
    company: 'FinanceFlow',
    companySize: 'Series C Fintech',
    avatar: 'SC',
    rating: 5,
    result: '$15M new revenue in year 1',
    linkedin: true,
  },
  {
    content: 'We interviewed 12 consulting firms. ZeeJay Labs was the only one that truly understood our scale challenges and delivered a solution that actually worked under extreme load. Black Friday went flawlessly—zero downtime, record sales. Worth every penny.',
    author: 'Michael Rodriguez',
    role: 'VP of Engineering',
    company: 'RetailHub',
    companySize: 'Fortune 500 Retailer',
    avatar: 'MR',
    rating: 5,
    result: '$24M Black Friday sales',
    linkedin: true,
  },
  {
    content: 'The attention to security and compliance was exceptional. ZeeJay Labs navigated HIPAA requirements flawlessly while delivering a system our staff actually loves using. Patient care quality improved measurably, and we\'re saving millions annually.',
    author: 'Dr. Emily Watson',
    role: 'Chief Medical Officer',
    company: 'HealthSync',
    companySize: '75-Facility Network',
    avatar: 'EW',
    rating: 5,
    result: '$18M saved annually',
    linkedin: true,
  },
  {
    content: 'What impressed me most was their strategic thinking. They didn\'t just code what we asked for—they challenged our assumptions and proposed solutions that were genuinely better. Six months in, our platform is processing 5M+ transactions daily with perfect reliability.',
    author: 'James Park',
    role: 'Co-Founder & CEO',
    company: 'PayFlow',
    companySize: 'Series B Fintech',
    avatar: 'JP',
    rating: 5,
    result: '5M+ daily transactions',
    linkedin: true,
  },
  {
    content: 'Best tech investment we ever made. The team moved fast, communicated clearly, and delivered exactly what they promised. Our conversion rate jumped 45% within weeks of launch. They\'re not vendors—they\'re true partners in our growth.',
    author: 'Lisa Martinez',
    role: 'Chief Product Officer',
    company: 'ShopDirect',
    companySize: 'E-Commerce Unicorn',
    avatar: 'LM',
    rating: 5,
    result: '45% conversion increase',
    linkedin: true,
  },
  {
    content: 'ZeeJay Labs took our legacy mess and turned it into a modern, cloud-native system without disrupting operations. The migration was seamless, costs dropped 60%, and performance improved dramatically. Exactly what we needed.',
    author: 'Robert Kim',
    role: 'CTO',
    company: 'DataCorp',
    companySize: 'Enterprise SaaS',
    avatar: 'RK',
    rating: 5,
    result: '60% cost reduction',
    linkedin: true,
  },
];

export function Testimonials() {
  return (
    <section className="py-20 md:py-32 relative overflow-hidden bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-blue-600/5 to-transparent rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20 max-w-4xl mx-auto animate-fadeInUp">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-100 mb-6">
            <Star size={16} className="text-amber-600 fill-amber-600" />
            <span className="text-sm text-amber-700 font-semibold uppercase tracking-wider">
              Client Testimonials
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6">
            <span className="gradient-text-premium">Don't Take Our Word.</span>
            <br />
            <span className="text-gray-900">Hear From Our Clients.</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Industry leaders trust ZeeJay Labs to deliver transformational results. Here's what they have to say about working with us.
          </p>
        </div>

        {/* Stats Banner */}
        <div className="grid md:grid-cols-4 gap-6 mb-16 animate-fadeInUp">
          {[
            { value: '4.9/5', label: 'Average Rating' },
            { value: '98%', label: 'Would Recommend' },
            { value: '500+', label: 'Client Reviews' },
            { value: '99%', label: 'Retention Rate' }
          ].map((stat, idx) => (
            <div key={idx} className="text-center p-6 bg-white rounded-2xl shadow-premium">
              <div className="text-3xl font-bold gradient-text-primary mb-1">{stat.value}</div>
              <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="premium-card p-8 rounded-2xl hover-lift flex flex-col animate-fadeInUp"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Quote Icon & Rating */}
              <div className="flex items-center justify-between mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 text-white flex items-center justify-center shadow-lg">
                  <Quote size={24} />
                </div>
                <div className="flex gap-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-amber-500 fill-amber-500" />
                  ))}
                </div>
              </div>

              {/* Testimonial Content */}
              <p className="text-gray-700 leading-relaxed mb-6 flex-grow">
                "{testimonial.content}"
              </p>

              {/* Result Highlight */}
              <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-xl">
                <div className="text-xs text-green-700 font-semibold uppercase tracking-wider mb-1">Result Achieved</div>
                <div className="text-sm font-bold text-green-800">{testimonial.result}</div>
              </div>

              {/* Author Info */}
              <div className="flex items-start gap-4 pt-6 border-t border-gray-100">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
                  {testimonial.avatar}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-bold text-gray-900">{testimonial.author}</div>
                    {testimonial.linkedin && (
                      <LinkedinIcon size={14} className="text-blue-600" />
                    )}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">{testimonial.role}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Building2 size={12} className="text-gray-400" />
                    <span className="text-xs text-gray-500">{testimonial.company}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">{testimonial.companySize}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Video Testimonial Placeholder */}
        <div style={{backgroundColor: "#5743ff"}}
             className="rounded-3xl p-12 text-white text-center shadow-premium-lg animate-fadeInUp">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <Star size={16} className="text-white fill-white" />
              <span className="text-sm font-semibold uppercase tracking-wider">Featured</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-extrabold mb-4">
              See why CTOs at Fortune 500 companies choose ZeeJay Labs
            </h3>
            <p className="text-lg md:text-xl mb-8 opacity-95">
              Watch exclusive interviews with our clients discussing their transformation journeys and the measurable impact we delivered.
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-10 py-5 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all hover:shadow-2xl hover:-translate-y-1 inline-flex items-center gap-2"
            >
              Schedule Your Consultation
              <Quote size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}